import {
  EmptyState,
  FormCard,
  FormItem,
  NoDataIcon,
  Select,
  theme
} from '@lmc/ui-kit'
import type {
  GroupedOption,
  Option
} from '@lmc/ui-kit/src/dataEntry/MultiSelect/MultiSelect'
import { Button, Text } from '@lmc/ui-kit/src/general'
import { Tooltip } from 'antd'
import type { DefaultOptionType } from 'antd/es/select'
import useFeatureFlags from 'hooks/useFeatureFlags'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useParams } from 'react-router-dom'
import styled from 'styled-components'
import type { ModalSectionProps } from '../RotaPattern/utils'
import { getRequiredRule } from '../utils'

type ServiceUserDetailsProps = {
  preferredPeopleNominative?: string
  residentOptions?: GroupedOption<Option<string>>[]
  fundingTypeOptions?: DefaultOptionType[]
  addressOptions?: DefaultOptionType[]
  disableResident?: boolean
  disableAddress?: boolean
  afterFundingType?: React.ReactNode
} & ModalSectionProps

const ServiceUserDetails: FC<ServiceUserDetailsProps> = ({
  preferredPeopleNominative,
  residentOptions,
  fundingTypeOptions,
  addressOptions,
  disableResident,
  disableAddress,
  afterFundingType,
  formData
}) => {
  const { t } = useTranslation()

  const params = useParams()

  const flags = useFeatureFlags({
    flagNames: ['rostering_admin_editing_flexibility']
  })?.data

  const flagsRosteringAdminEditingFlexibility =
    flags?.rostering_admin_editing_flexibility?.enabled

  const hasPrefilledResident = residentOptions
    ?.flatMap((group) => group.options)
    ?.some((option) => option.value === params?.id)

  const filterOption = (input: string, option?: Option<string>) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  const selectedResident = formData?.subjectId

  return (
    <FormCard
      level={2}
      title={t('SERVICE_USER_DETAILS', { variable: preferredPeopleNominative })}
    >
      <FormItem
        name="subjectId"
        label={<Text>{preferredPeopleNominative || t('SERVICE_USER')}</Text>}
        rules={[
          getRequiredRule(preferredPeopleNominative || t('SERVICE_USER'))
        ]}
      >
        <Select
          allowClear
          optionFilterProp="children"
          filterOption={filterOption}
          showSearch
          disabled={hasPrefilledResident || disableResident}
          notFoundContent={
            <EmptyState
              containerStyle={{ paddingTop: 24 }}
              title={
                <EmptyStateWrapper>
                  <Text>
                    {t('SELECT_EMPTY_STATE_RESIDENT', {
                      variable: preferredPeopleNominative
                    })}
                  </Text>
                </EmptyStateWrapper>
              }
              description={
                <Link to="/residents/list" target="_blank" rel="noreferrer">
                  <Button variant="primary">{t('ADD')}</Button>
                </Link>
              }
              icon={
                <NoDataIconWrapper>
                  <NoDataIcon />
                </NoDataIconWrapper>
              }
            />
          }
        >
          {residentOptions &&
            residentOptions.map((group) => (
              <Select.OptGroup key={group.label} label={group.label}>
                {group.options.map((option) => (
                  <Select.Option
                    key={String(option.value)}
                    value={option.value}
                    label={option.label}
                  >
                    <OptionContainer>
                      <ImageContainer>{option.image} </ImageContainer>
                      <StyledText>{option.label}</StyledText>
                    </OptionContainer>
                  </Select.Option>
                ))}
              </Select.OptGroup>
            ))}
        </Select>
      </FormItem>
      <FormItem
        name="fundingTypeId"
        label={<Text>{t('FUNDING_TYPE')}</Text>}
        rules={[getRequiredRule(t('FUNDING_TYPE'))]}
        extra={<Text>{t('PLEASE_SELECT_FUNDING')}</Text>}
      >
        <Select
          // @ts-ignore
          options={fundingTypeOptions}
          optionFilterProp="children"
          filterOption={filterOption}
          showSearch
          notFoundContent={
            <EmptyState
              title={
                <EmptyStateWrapper>
                  <Text>{t('SELECT_EMPTY_STATE_FUNDING_1')}</Text>
                  <Text>{t('SELECT_EMPTY_STATE_FUNDING_2')}</Text>
                </EmptyStateWrapper>
              }
              description={
                <Link
                  to={`/residents/${selectedResident}/care-package-and-patterns`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Button variant="primary" disabled={!selectedResident}>
                    {t('EDIT')}
                  </Button>
                </Link>
              }
              icon={
                <NoDataIconWrapper>
                  <NoDataIcon />
                </NoDataIconWrapper>
              }
            />
          }
        />
      </FormItem>
      {afterFundingType}
      <Tooltip
        color={theme.color.neutral.color3}
        title={
          disableAddress ? (
            <ContentContainer>
              <Text>
                {flagsRosteringAdminEditingFlexibility
                  ? t('DETAIL_EDITABLE_BY_ADMIN')
                  : t('DETAIL_CANNOT_BE_EDITED')}
              </Text>
            </ContentContainer>
          ) : (
            ''
          )
        }
        open={disableAddress ? undefined : false}
        overlayInnerStyle={{ width: '450px', padding: '8px 16px' }}
      >
        <FormItem
          name="addressId"
          label={<Text>{t('ADDRESS')}</Text>}
          rules={[getRequiredRule(t('ADDRESS'))]}
          extra={<Text>{t('SELECT_ADDRESS')}</Text>}
        >
          <Select
            // @ts-ignore
            options={addressOptions}
            allowClear
            optionFilterProp="children"
            filterOption={filterOption}
            showSearch
            disabled={disableAddress}
            notFoundContent={
              <EmptyState
                title={
                  <EmptyStateWrapper>
                    <Text>
                      {t('SELECT_EMPTY_STATE_ADDRESS', {
                        variable: preferredPeopleNominative
                      })}
                    </Text>
                  </EmptyStateWrapper>
                }
                description={
                  <Link
                    to={`/residents/${selectedResident}/contact-details`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <Button disabled={!selectedResident} variant="primary">
                      {t('ADD')}
                    </Button>
                  </Link>
                }
                icon={
                  <NoDataIconWrapper>
                    <NoDataIcon />
                  </NoDataIconWrapper>
                }
              />
            }
          />
        </FormItem>
      </Tooltip>
    </FormCard>
  )
}

const OptionContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`

const ImageContainer = styled.div`
  display: flex;
  align-items: center;
`

const EmptyStateWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const NoDataIconWrapper = styled.div`
  scale: 0.5;
  height: 50px;
  display: flex;
  align-items: center;
`

const StyledText = styled(Text)<{ isCapitalized?: boolean }>`
  text-transform: ${(props) => (props.isCapitalized ? 'capitalized' : 'none')};
  color: ${(props) => props.theme.color.neutral.color3};
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  color: ${(props) => props.theme.color.neutral.color11};
`

export default ServiceUserDetails
